import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Calendar, MapPin, Clock, QrCode, CheckCircle, X, Download, FileText, ExternalLink, Radio, ArrowLeft } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import axios from 'axios';
import { isAuthenticated, getAuthHeader } from '../services/otpAuthService';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EventPopulated {
  _id: string;
  nome: string;
  tipologia?: string;
  tipologiaId?: { _id: string; nome: string; colore: string };
  dataEvento: string;
  oraInizio?: string;
  oraFine?: string;
  luogo?: string;
  indirizzo?: string;
  linkMappa?: string;
  descrizione?: string;
  /** Punti dell'ordine del giorno (assente negli eventi di prima). */
  ordineDelGiorno?: string[];
  emailContent?: string;
  stato: string;
  enableQrCode?: boolean;
}

interface VerbaleAllegato {
  _id: string;
  filename: string;
  path: string;
  mimetype?: string;
  size?: number;
}

interface VerbaleData {
  contenuto: string;
  pubblicatoAt?: string | null;
  allegati: VerbaleAllegato[];
}

interface Invitation {
  _id: string;
  eventId: EventPopulated;
  verbale?: VerbaleData | null;
  checkedIn: boolean;
  checkedInAt?: string;
  qrCodeDataUrl?: string;
  expiresAt?: string;
  createdAt: string;
}

// Dati per la card diretta (sia banner evento collegato sia dirette pubbliche)
interface LiveCardData {
  slug: string;
  title: string;
  subtitle?: string;
  coverImage?: string | null;
  status: string;
}

interface PublicLive extends LiveCardData {
  _id: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const UPLOADS_BASE = API_BASE.replace(/\/api$/, '');
const coverSrc = (url?: string | null) =>
  url ? (/^https?:\/\//.test(url) ? url : `${UPLOADS_BASE}${url}`) : '';

const TIPOLOGIA_COLORS: Record<string, string> = {
  'Assemblea Nazionale': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  'Assemblea Regionale': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  'Assemblea Provinciale': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  'Convegno': 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
  'Corso di Formazione': 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  'Seminario': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  'Riunione': 'bg-slate-100 text-slate-800 dark:bg-slate-900/40 dark:text-slate-300',
  'Evento Sociale': 'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300',
  'Segreteria Nazionale': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300',
  'Altro': 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

async function getLiveForEvent(eventId: string): Promise<LiveCardData | null> {
  const res = await axios.get(`${API_BASE}/live/by-event/${eventId}`, { validateStatus: s => s === 200 || s === 204 });
  return res.status === 200
    ? { slug: res.data.slug, status: res.data.status, title: res.data.title, subtitle: res.data.subtitle, coverImage: res.data.coverImage }
    : null;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })} alle ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
}

const MONTHS_SHORT = ['GEN', 'FEB', 'MAR', 'APR', 'MAG', 'GIU', 'LUG', 'AGO', 'SET', 'OTT', 'NOV', 'DIC'];

// Converte un colore esadecimale in rgba (per sfondi/bordi morbidi dall'accento tipologia).
function hexToRgba(hex: string, alpha: number): string {
  let h = (hex || '').replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return `rgba(37, 99, 235, ${alpha})`; // fallback blue-600
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

function dateParts(dateStr: string) {
  const d = new Date(dateStr);
  return {
    day: d.toLocaleDateString('it-IT', { day: '2-digit' }),
    month: MONTHS_SHORT[d.getMonth()] || '',
    weekday: d.toLocaleDateString('it-IT', { weekday: 'long' }),
    full: d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }),
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const URL_RE = /(https?:\/\/[^\s<]+)/g;

// La descrizione evento può essere testo semplice (con a-capo, dai rappresentanti)
// o HTML ricco (emailContent admin). Per il testo semplice preserviamo gli a-capo
// (via white-space: pre-wrap nel contenitore) ed escapiamo l'HTML; per l'HTML
// lo rendiamo così com'è. Ritorna { html, isHtml }.
function renderEventDescription(raw: string): { html: string; isHtml: boolean } {
  const isHtml = /<[a-z][\s\S]*>/i.test(raw);
  if (isHtml) return { html: raw, isHtml: true };
  const html = escapeHtml(raw).replace(URL_RE, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
  return { html, isHtml: false };
}

// ─── QR Modal ─────────────────────────────────────────────────────────────────

interface QrModalProps {
  invitation: Invitation;
  onClose: () => void;
}

const QrModal: React.FC<QrModalProps> = ({ invitation, onClose }) => {
  const { eventId, qrCodeDataUrl } = invitation;
  const closeRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleDownloadPdf = async () => {
    try {
      setPdfLoading(true);
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
      const res = await axios.get(`${apiBase}/associate-events/my-events/${eventId._id}/qrcode/pdf`, {
        responseType: 'blob',
        headers: getAuthHeader(),
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `qrcode-${eventId.nome.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      let msg = 'Errore nella generazione del PDF';
      if (err?.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).message || msg; } catch { /* keep default */ }
      }
      toast.error(msg);
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleDownload = () => {
    if (!qrCodeDataUrl) return;
    const link = document.createElement('a');
    link.href = qrCodeDataUrl;
    link.download = `qrcode-${eventId.nome.replace(/\s+/g, '-').toLowerCase()}.png`;
    link.click();
  };

  // Accento dinamico = colore della tipologia (fallback blue-600).
  const accent = eventId.tipologiaId?.colore || '#2563eb';
  const dp = dateParts(eventId.dataEvento);
  const timeRange = [eventId.oraInizio, eventId.oraFine].filter(Boolean).join(' – ');
  const tipName = eventId.tipologiaId?.nome || eventId.tipologia;
  const rawDesc = eventId.emailContent || eventId.descrizione || '';
  const desc = renderEventDescription(rawDesc);
  const puntiOdg = Array.isArray(eventId.ordineDelGiorno) ? eventId.ordineDelGiorno.filter((x) => typeof x === 'string' && x.trim()) : [];

  // Entrata animata (CSS-only via stato di mount, nessuna dipendenza dal config Tailwind).
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(r);
  }, []);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 transition-opacity duration-200 ${shown ? 'opacity-100' : 'opacity-0'}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Dettagli evento ${eventId.nome}`}
    >
      <div
        className={`relative w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden rounded-3xl bg-white dark:bg-gray-800 shadow-2xl ring-1 ring-black/5 dark:ring-white/10 transition-all duration-300 ease-out ${shown ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header a banda colorata (accento = tipologia) ───────────────── */}
        <div
          className="relative shrink-0 px-5 pt-5 pb-5 text-white overflow-hidden"
          style={{ background: accent }}
        >
          {/* Atmosfera: luce, glow, texture a puntini */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-black/35" />
          <div className="absolute -top-12 -right-10 w-44 h-44 rounded-full bg-white/15 blur-2xl" />
          <div
            className="absolute inset-0 opacity-[0.13]"
            style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '15px 15px' }}
          />

          <button
            ref={closeRef}
            onClick={onClose}
            className="absolute top-3.5 right-3.5 p-1.5 rounded-full bg-white/15 hover:bg-white/30 text-white backdrop-blur-sm transition-colors"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="relative flex items-start gap-4">
            {/* Tear-off calendario */}
            <div className="shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-2xl bg-white shadow-lg">
              <span className="text-[10px] font-extrabold tracking-[0.15em] leading-none" style={{ color: accent }}>
                {dp.month}
              </span>
              <span className="text-[26px] font-extrabold leading-none text-gray-900 mt-1">{dp.day}</span>
            </div>

            <div className="min-w-0 pr-8">
              {tipName && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-2xs font-semibold bg-white/20 text-white backdrop-blur-sm ring-1 ring-white/25">
                  {tipName}
                </span>
              )}
              <h2 className="text-lg font-bold leading-snug mt-1.5 drop-shadow-sm break-words">
                {eventId.nome}
              </h2>
              <p className="text-xs text-white/85 mt-1 capitalize">
                {dp.weekday} {dp.full}{timeRange && ` · ${timeRange}`}
              </p>
            </div>
          </div>
        </div>

        {/* ── Corpo scrollabile ───────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

          {/* Griglia info: orario + dove */}
          {(timeRange || eventId.luogo || eventId.indirizzo) && (
            <div className="space-y-3">
              {timeRange && (
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: hexToRgba(accent, 0.12), color: accent }}>
                    <Clock className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-2xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Orario</p>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{timeRange}</p>
                  </div>
                </div>
              )}
              {(eventId.luogo || eventId.indirizzo) && (
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: hexToRgba(accent, 0.12), color: accent }}>
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-2xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Dove</p>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100 break-words">
                      {eventId.luogo || eventId.indirizzo}
                    </p>
                    {eventId.luogo && eventId.indirizzo && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 break-words mt-0.5">{eventId.indirizzo}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pulsante mappa */}
          {eventId.linkMappa && (
            <a
              href={eventId.linkMappa}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold transition hover:brightness-95 dark:hover:brightness-110"
              style={{ background: hexToRgba(accent, 0.1), color: accent }}
            >
              <MapPin className="w-4 h-4" />
              Apri in Google Maps
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}

          {/* Presenza registrata */}
          {invitation.checkedIn && (
            <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/25 ring-1 ring-emerald-200 dark:ring-emerald-800">
              <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span className="text-sm font-medium text-emerald-800 dark:text-emerald-300">Presenza registrata</span>
            </div>
          )}

          {/* Descrizione / ordine del giorno (a-capo preservati per il testo semplice) */}
          {rawDesc && (
            <div>
              <p className="text-2xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">Dettagli</p>
              <div className="rounded-2xl bg-gray-50 dark:bg-gray-900/60 ring-1 ring-gray-100 dark:ring-gray-700 p-4 max-h-72 overflow-y-auto overflow-x-hidden">
                <div
                  className={`testo-ricco text-gray-700 dark:text-gray-300 break-words [&_img]:max-w-full [&_img]:h-auto [&_iframe]:max-w-full [&_iframe]:w-full [&_table]:!w-full [&_table]:!max-w-full [&_*]:max-w-full ${desc.isHtml ? '' : 'whitespace-pre-wrap'}`}
                  dangerouslySetInnerHTML={{ __html: desc.html }}
                />
              </div>
            </div>
          )}

          {/* Ordine del giorno (campo apposito) */}
          {puntiOdg.length > 0 && (
            <div>
              <p className="text-2xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">Ordine del giorno</p>
              <ol className="list-decimal space-y-1 rounded-2xl bg-gray-50 p-4 pl-9 text-sm text-gray-700 ring-1 ring-gray-100 dark:bg-gray-900/60 dark:text-gray-300 dark:ring-gray-700">
                {puntiOdg.map((p, i) => <li key={i}>{p}</li>)}
              </ol>
            </div>
          )}

          {/* QR "pass" (solo se abilitato) */}
          {qrCodeDataUrl ? (
            <div
              className="relative rounded-2xl border-2 border-dashed p-5 flex flex-col items-center gap-3"
              style={{ borderColor: hexToRgba(accent, 0.35), background: hexToRgba(accent, 0.04) }}
            >
              <span className="inline-flex items-center gap-1.5 text-2xs font-bold uppercase tracking-[0.15em]" style={{ color: accent }}>
                <QrCode className="w-3.5 h-3.5" />
                Il tuo pass
              </span>
              <div className="bg-white p-3 rounded-2xl shadow-sm">
                <img src={qrCodeDataUrl} alt="QR Code evento" className="w-44 h-44 object-contain" />
              </div>
              <p className="text-xs text-center text-gray-500 dark:text-gray-400 max-w-[15rem]">
                Mostra questo codice all'ingresso per registrare la tua presenza.
              </p>
            </div>
          ) : (
            !invitation.checkedIn && (
              <p className="text-center text-xs text-gray-400 dark:text-gray-500">
                La tua presenza verrà registrata dall'organizzatore dell'evento.
              </p>
            )
          )}
        </div>

        {/* ── Footer azioni (sticky, solo con QR) ─────────────────────────── */}
        {qrCodeDataUrl && (
          <div className="shrink-0 border-t border-gray-100 dark:border-gray-700 p-4 flex gap-3 bg-white dark:bg-gray-800">
            <button
              onClick={handleDownload}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl transition hover:brightness-95"
              style={{ background: accent }}
            >
              <Download className="w-4 h-4" />
              Salva PNG
            </button>
            <button
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl ring-1 transition hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50"
              style={{ color: accent, borderColor: accent, ['--tw-ring-color' as any]: hexToRgba(accent, 0.4) }}
            >
              <FileText className="w-4 h-4" />
              {pdfLoading ? 'Genero…' : 'Salva PDF'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Event Card ───────────────────────────────────────────────────────────────

type LiveInfo = LiveCardData;

// ─── Live Card (copertina + overlay + badge stato + CTA) ───────────────────────

const LIVE_STATUS_META: Record<string, { badge: string; badgeCls: string; cta: string }> = {
  live:   { badge: 'IN ONDA',  badgeCls: 'bg-red-600 text-white',    cta: 'Segui la diretta' },
  paused: { badge: 'IN PAUSA', badgeCls: 'bg-amber-500 text-white',  cta: 'Entra nella diretta' },
  ended:  { badge: 'CONCLUSA', badgeCls: 'bg-slate-600 text-white',  cta: 'Rivedi la diretta' },
};

const LiveCard: React.FC<{ live: LiveCardData; className?: string }> = ({ live, className = '' }) => {
  const meta = LIVE_STATUS_META[live.status] || LIVE_STATUS_META.ended;
  const isLive = live.status === 'live';
  const cover = coverSrc(live.coverImage);

  return (
    <Link
      to={`/diretta/${live.slug}`}
      className={`group relative block overflow-hidden rounded-xl border border-black/5 dark:border-white/10 shadow-sm hover:shadow-md transition-shadow min-h-[116px] ${className}`}
      aria-label={`Apri la diretta ${live.title}`}
    >
      {/* Sfondo: copertina o gradiente brand */}
      {cover ? (
        <img
          src={cover}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-900" />
      )}
      {/* Watermark icona quando manca la copertina */}
      {!cover && (
        <Radio className="absolute -right-3 -bottom-3 w-24 h-24 text-white/10" />
      )}
      {/* Overlay per leggibilita */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/15" />

      {/* Contenuto */}
      <div className="relative h-full p-4 flex flex-col justify-between min-h-[116px]">
        <div>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-2xs font-bold tracking-wide ${meta.badgeCls}`}>
            {isLive && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
            {meta.badge}
          </span>
        </div>
        <div>
          <h3 className="text-sm font-bold text-white leading-tight line-clamp-1 drop-shadow-sm">
            {live.title}
          </h3>
          {live.subtitle && (
            <p className="text-2xs text-white/80 line-clamp-1 mt-0.5">{live.subtitle}</p>
          )}
          <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-white">
            {meta.cta}
            <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
};

interface EventCardProps {
  invitation: Invitation;
  isFuture: boolean;
  onShowQr: (inv: Invitation) => void;
  onApriVerbale: (inv: Invitation) => void;
  liveInfo?: LiveInfo | null;
}

const EventCard: React.FC<EventCardProps> = ({ invitation, isFuture, onShowQr, onApriVerbale, liveInfo }) => {
  const { eventId, checkedIn, checkedInAt } = invitation;
  const isCancelled = eventId.stato === 'ANNULLATO';
  const tipColor = eventId.tipologiaId
    ? '' // usa stile inline con colore dinamico
    : (TIPOLOGIA_COLORS[eventId.tipologia || ''] ?? TIPOLOGIA_COLORS['Altro']);

  // Status badge — priorita: annullato > partecipato > stato evento > fallback temporale
  const stato = eventId.stato;
  let statusBadge: React.ReactNode;
  if (isCancelled) {
    statusBadge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
        Annullato
      </span>
    );
  } else if (checkedIn) {
    statusBadge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
        <CheckCircle className="w-3 h-3" /> Partecipato
      </span>
    );
  } else if (stato === 'IN_CORSO') {
    statusBadge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
        In corso
      </span>
    );
  } else if (stato === 'CONCLUSO') {
    statusBadge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
        Concluso
      </span>
    );
  } else if (isFuture) {
    statusBadge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
        In arrivo
      </span>
    );
  } else {
    statusBadge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
        Non partecipato
      </span>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-col gap-3 shadow-sm">
      {/* Top row: name + tipologia */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white leading-tight flex-1 min-w-0">
          {eventId.nome}
        </h3>
        <span
          className={`inline-block shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${tipColor}`}
          style={eventId.tipologiaId ? { backgroundColor: eventId.tipologiaId.colore + '20', color: eventId.tipologiaId.colore } : {}}
        >
          {eventId.tipologiaId?.nome || eventId.tipologia}
        </span>
      </div>

      {/* Meta info */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1">
          <Calendar className="w-4 h-4 shrink-0" />
          {formatDate(eventId.dataEvento)}
          {eventId.oraInizio && (
            <>
              <Clock className="w-4 h-4 shrink-0 ml-1" />
              {eventId.oraInizio}
            </>
          )}
        </span>
        {eventId.luogo && (
          <span className="flex items-center gap-1">
            <MapPin className="w-4 h-4 shrink-0" />
            {eventId.luogo}
          </span>
        )}
      </div>

      {/* Status + CTA */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2 flex-wrap">
          {statusBadge}
          {checkedIn && checkedInAt && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Presenza registrata il {formatDateTime(checkedInAt)}
            </span>
          )}
          {!isFuture && !checkedIn && !isCancelled && (
            <span className="text-xs text-gray-400 dark:text-gray-500">Evento concluso</span>
          )}
          {isCancelled && (
            <span className="text-xs text-gray-400 dark:text-gray-500">Evento annullato</span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {invitation.verbale && (
            <button
              onClick={() => onApriVerbale(invitation)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <FileText className="w-3.5 h-3.5" />
              Verbale
            </button>
          )}
          {isFuture && !isCancelled && (
            <button
              onClick={() => onShowQr(invitation)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <Calendar className="w-3.5 h-3.5" />
              Dettagli
            </button>
          )}
        </div>
      </div>

      {/* Card diretta collegata: visibile in onda, in pausa o conclusa (rivedibile) */}
      {liveInfo && liveInfo.status !== 'draft' && (
        <LiveCard live={liveInfo} className="mt-1" />
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const MyEventsPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [futureEvents, setFutureEvents] = useState<Invitation[]>([]);
  const [pastEvents, setPastEvents] = useState<Invitation[]>([]);
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null);
  const [liveByEvent, setLiveByEvent] = useState<Record<string, LiveInfo | null>>({});
  const [publicLives, setPublicLives] = useState<PublicLive[]>([]);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/associate-events/my-events`, {
        headers: getAuthHeader() as Record<string, string>,
      });
      if (response.data.success) {
        const future: Invitation[] = response.data.futureEvents || [];
        const past: Invitation[] = response.data.pastEvents || [];
        setFutureEvents(future);
        setPastEvents(past);

        // Carica l'info diretta collegata per TUTTI gli eventi (anche conclusi): la card
        // resta visibile a diretta/evento conclusi per consentire di rivedere la diretta.
        const allEventIds = Array.from(new Set([...future, ...past].map((inv) => inv.eventId._id)));
        const liveResults = await Promise.all(
          allEventIds.map(async (id) => {
            try { return { id, info: await getLiveForEvent(id) }; }
            catch { return { id, info: null }; }
          })
        );
        const liveMap: Record<string, LiveInfo | null> = {};
        liveResults.forEach(({ id, info }) => { liveMap[id] = info; });
        setLiveByEvent(liveMap);

        // Dirette pubbliche non collegate ad alcun evento (rivedibili da tutti gli associati)
        try {
          const pub = await axios.get(`${API_BASE}/live/public`);
          setPublicLives(pub.data?.items || []);
        } catch { /* best-effort: la sezione dirette resta vuota */ }
      }
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Sessione scaduta, effettua nuovamente l\'accesso');
        navigate('/accesso-associati');
      } else {
        toast.error('Errore nel caricamento degli eventi');
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated()) {
      toast.error('Devi effettuare l\'accesso');
      navigate('/accesso-associati');
      return;
    }
    loadEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [navigate, loadEvents]);

  const totalEvents = futureEvents.length + pastEvents.length;
  const apriVerbale = (inv: Invitation) => navigate(`/area-riservata/eventi/${inv.eventId._id}/verbale`);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-2xl mx-auto px-4 py-8">

        {/* Torna indietro */}
        <button
          onClick={() => navigate('/area-riservata')}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Area Riservata
        </button>

        {/* Page header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 bg-blue-100 dark:bg-blue-900/40 rounded-xl">
            <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">I Miei Eventi</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              I tuoi inviti e gli eventi a cui sei stato invitato
            </p>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Caricamento eventi...</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && totalEvents === 0 && publicLives.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
            <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-2xl">
              <Calendar className="w-10 h-10 text-gray-400 dark:text-gray-500" />
            </div>
            <div>
              <p className="text-base font-medium text-gray-700 dark:text-gray-300">
                Non hai inviti ad eventi
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Gli eventi a cui sei invitato appariranno qui.
              </p>
            </div>
          </div>
        )}

        {/* Dirette pubbliche (non collegate a un evento) */}
        {!loading && publicLives.length > 0 && (
          <section className="mb-8">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Dirette ({publicLives.length})
            </h2>
            <div className="flex flex-col gap-3">
              {publicLives.map((live) => (
                <LiveCard key={live._id} live={live} />
              ))}
            </div>
          </section>
        )}

        {/* Future events */}
        {!loading && futureEvents.length > 0 && (
          <section className="mb-8">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Prossimi eventi ({futureEvents.length})
            </h2>
            <div className="flex flex-col gap-3">
              {futureEvents.map((inv) => (
                <EventCard
                  key={inv._id}
                  invitation={inv}
                  isFuture={true}
                  onShowQr={setSelectedInvitation}
                  onApriVerbale={apriVerbale}
                  liveInfo={liveByEvent[inv.eventId._id]}
                />
              ))}
            </div>
          </section>
        )}

        {/* Past events */}
        {!loading && pastEvents.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Eventi passati ({pastEvents.length})
            </h2>
            <div className="flex flex-col gap-3">
              {pastEvents.map((inv) => (
                <EventCard
                  key={inv._id}
                  invitation={inv}
                  isFuture={false}
                  onShowQr={setSelectedInvitation}
                  onApriVerbale={apriVerbale}
                  liveInfo={liveByEvent[inv.eventId._id]}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* QR Code Modal */}
      {selectedInvitation && (
        <QrModal
          invitation={selectedInvitation}
          onClose={() => setSelectedInvitation(null)}
        />
      )}
    </div>
  );
};

export default MyEventsPage;
